import { useContext } from "react";

import {
  generatePath,
  UNSAFE_RouteContext,
  useLocation,
  useParams,
} from "react-router-dom";
import isEqual from "lodash/isEqual";
import { retention as retentionLimits } from "utils/limits";

import { RouteStateContext } from "utils/routeState";

export const useRoutes = () => {
  const [routeState] = useContext(RouteStateContext);
  const ts = routeState.search.t;

  const withRange = (
    path: string,
    opts?: {
      search: Record<string, string | undefined>;
    },
  ): string => {
    const search = {
      ...opts?.search,
    };
    if (ts) {
      search.t = ts;
    }

    return formatPath(path, search);
  };
  return {
    changelog(): string {
      return "/changelog";
    },
    oauthAuthorization(): string {
      return "/oauth/authorize";
    },
    database(databaseId: string): string {
      return withRange(`/databases/${databaseId}`);
    },
    databaseBackend(databaseId: string, backendId: string): string {
      return withRange(`/databases/${databaseId}/backends/${backendId}`);
    },
    databaseBackendLogs(databaseId: string, backendId: string): string {
      return withRange(`/databases/${databaseId}/backends/${backendId}/logs`);
    },
    databaseBackendQueries(databaseId: string, backendId: string): string {
      return withRange(
        `/databases/${databaseId}/backends/${backendId}/queries`,
      );
    },
    databaseBackendWaitEvents(databaseId: string, backendId: string): string {
      return withRange(
        `/databases/${databaseId}/backends/${backendId}/wait_events`,
      );
    },
    databaseBackends(databaseId: string): string {
      return withRange(`/databases/${databaseId}/backends`);
    },
    databaseBackendAtTime(
      databaseId: string,
      backendPid: number,
      ts: number,
    ): string {
      return withRange(`/databases/${databaseId}/backends/${backendPid}@${ts}`);
    },
    databaseBackendsAtTime(
      databaseId: string,
      from: number,
      to: number,
    ): string {
      return withRange(`/databases/${databaseId}/backends?t=${from}-${to}`);
    },
    databaseFunction(databaseId: string, functionId: string): string {
      return withRange(`/databases/${databaseId}/functions/${functionId}`);
    },
    databaseFunctions(databaseId: string): string {
      return withRange(`/databases/${databaseId}/functions`);
    },
    databaseIndex(databaseId: string, indexId: string): string {
      return withRange(`/databases/${databaseId}/indexes/${indexId}`);
    },
    databaseIndexCheck(databaseId: string, queryId: string): string {
      return withRange(
        `/databases/${databaseId}/queries/${queryId}/indexcheck`,
      );
    },
    databaseQueryIndexAdvisor(databaseId: string, queryId: string): string {
      return withRange(
        `/databases/${databaseId}/queries/${queryId}/indexadvisor`,
      );
    },
    databaseIndexAdvisor(databaseId: string): string {
      return withRange(`/databases/${databaseId}/indexadvisor`);
    },
    databaseIndexAdvisorIndexingEngine(databaseId: string): string {
      return withRange(`/databases/${databaseId}/indexadvisor/indexing_engine`);
    },
    databaseIndexAdvisorIndexUnused(databaseId: string): string {
      return withRange(`/databases/${databaseId}/indexadvisor/index_unused`);
    },
    databaseIndexAdvisorStatus(databaseId: string): string {
      return withRange(`/databases/${databaseId}/indexadvisor/status`);
    },
    databaseIndexAdvisorConfig(databaseId: string): string {
      return withRange(`/databases/${databaseId}/indexadvisor/config`);
    },
    databaseIndexes(databaseId: string): string {
      return withRange(`/databases/${databaseId}/indexes`);
    },
    databaseLogs(databaseId: string, logClassification?: string): string {
      const classificationPart = logClassification
        ? `/${logClassification}`
        : "";
      return withRange(`/databases/${databaseId}/logs${classificationPart}`);
    },
    databaseQueries(databaseId: string): string {
      return withRange(`/databases/${databaseId}/queries`);
    },
    databaseQueriesExplains(databaseId: string): string {
      return withRange(`/databases/${databaseId}/queries/explains`);
    },
    databaseQueriesAdvisor(databaseId: string): string {
      return withRange(`/databases/${databaseId}/queries/advisor`);
    },
    databaseQueriesAdvisorWorkbooks(databaseId: string): string {
      return withRange(`/databases/${databaseId}/queries/advisor/workbooks`);
    },
    databaseWorkbooks(databaseId: string): string {
      return withRange(`/databases/${databaseId}/workbooks`);
    },
    databaseWorkbooksNew(
      databaseId: string,
      queryId?: string,
      issueId?: string,
      explainId?: string,
    ): string {
      return formatPath(`/databases/${databaseId}/workbooks/new`, {
        queryId,
        issueId,
        explainId,
      });
    },
    databaseWorkbook(databaseId: string, workbookId: string): string {
      return `/databases/${databaseId}/workbooks/${workbookId}`;
    },
    databaseWorkbookCompare(
      databaseId: string,
      workbookId: string,
      planA?: string,
      planB?: string,
    ): string {
      return formatPath(
        `/databases/${databaseId}/workbooks/${workbookId}/compare`,
        { planA, planB },
      );
    },
    databaseWorkbookParams(databaseId: string, workbookId: string): string {
      return withRange(
        `/databases/${databaseId}/workbooks/${workbookId}/params`,
      );
    },
    databaseWorkbookActivity(databaseId: string, workbookId: string): string {
      return withRange(
        `/databases/${databaseId}/workbooks/${workbookId}/activity`,
      );
    },
    databaseWorkbookVariants(databaseId: string, workbookId: string): string {
      return withRange(
        `/databases/${databaseId}/workbooks/${workbookId}/variants`,
      );
    },
    databaseWorkbookVariantNew(
      databaseId: string,
      workbookId: string,
      opts?: {
        search: Record<string, string | undefined>;
      },
    ): string {
      return withRange(
        `/databases/${databaseId}/workbooks/${workbookId}/variants/new`,
        opts,
      );
    },
    databaseWorkbookVariant(
      databaseId: string,
      workbookId: string,
      variantId: string,
    ): string {
      return withRange(
        `/databases/${databaseId}/workbooks/${workbookId}/variants/${variantId}`,
      );
    },
    databaseWorkbookVariantRun(
      databaseId: string,
      workbookId: string,
      variantId: string,
    ): string {
      return withRange(
        `/databases/${databaseId}/workbooks/${workbookId}/variants/${variantId}/run`,
      );
    },
    databaseWorkbookVariantResult(
      databaseId: string,
      workbookId: string,
      variantId: string,
      resultId: string,
    ): string {
      return withRange(
        `/databases/${databaseId}/workbooks/${workbookId}/variants/${variantId}/results/${resultId}`,
      );
    },
    databaseQueriesSearch(databaseId: string): string {
      return withRange(`/databases/${databaseId}/queries/search`);
    },
    databaseQuery(databaseId: string, queryId: string): string {
      return withRange(`/databases/${databaseId}/queries/${queryId}`);
    },
    databaseQueryExplain(
      databaseId: string,
      queryId: string,
      explainId: string,
    ): string {
      return withRange(
        `/databases/${databaseId}/queries/${queryId}/explains/${explainId}`,
      );
    },
    databaseQueryExplainCompare(
      databaseId: string,
      queryId: string,
      explainId: string,
      compareTo?: string,
    ): string {
      return withRange(
        `/databases/${databaseId}/queries/${queryId}/explains/${explainId}`,
        {
          search: {
            view: "comparison",
            planA: explainId,
            planB: compareTo,
          },
        },
      );
    },
    databaseQueryExplains(databaseId: string, queryId: string): string {
      return withRange(`/databases/${databaseId}/queries/${queryId}/explains`);
    },
    databaseQueryLogs(databaseId: string, queryId: string): string {
      return withRange(`/databases/${databaseId}/queries/${queryId}/logs`);
    },
    databaseQuerySamples(databaseId: string, queryId: string): string {
      return withRange(`/databases/${databaseId}/queries/${queryId}/samples`);
    },
    databaseQueryTags(databaseId: string, queryId: string): string {
      return withRange(`/databases/${databaseId}/queries/${queryId}/tags`);
    },
    databaseTable(databaseId: string, tableId: string): string {
      return withRange(`/databases/${databaseId}/tables/${tableId}`);
    },
    databaseTableColumns(databaseId: string, tableId: string): string {
      return withRange(`/databases/${databaseId}/tables/${tableId}/columns`);
    },
    databaseTableConstraints(databaseId: string, tableId: string): string {
      return withRange(
        `/databases/${databaseId}/tables/${tableId}/constraints`,
      );
    },
    databaseTableIndexes(databaseId: string, tableId: string): string {
      return withRange(`/databases/${databaseId}/tables/${tableId}/indexes`);
    },
    databaseTableIndexSelection(databaseId: string, tableId: string): string {
      return withRange(
        `/databases/${databaseId}/tables/${tableId}/index_advisor`,
      );
    },
    databaseTablePartitions(databaseId: string, tableId: string): string {
      return withRange(`/databases/${databaseId}/tables/${tableId}/partitions`);
    },
    databaseTableQueries(databaseId: string, tableId: string): string {
      return withRange(`/databases/${databaseId}/tables/${tableId}/queries`);
    },
    databaseTableVacuum(databaseId: string, tableId: string): string {
      return withRange(`/databases/${databaseId}/tables/${tableId}/vacuum`);
    },
    databaseTableVacuumSimulator(databaseId: string, tableId: string): string {
      return withRange(
        `/databases/${databaseId}/tables/${tableId}/vacuum_simulator`,
      );
    },
    databaseTables(databaseId: string): string {
      return withRange(`/databases/${databaseId}/tables`);
    },
    databaseView(databaseId: string, viewId: string): string {
      return withRange(`/databases/${databaseId}/views/${viewId}`);
    },
    databaseViewColumns(databaseId: string, viewId: string): string {
      return withRange(`/databases/${databaseId}/views/${viewId}/columns`);
    },
    databaseViewIndexes(databaseId: string, viewId: string): string {
      return withRange(`/databases/${databaseId}/views/${viewId}/indexes`);
    },
    databaseViews(databaseId: string): string {
      return withRange(`/databases/${databaseId}/views`);
    },
    databaseWaitEvents(databaseId: string): string {
      return withRange(`/databases/${databaseId}/wait_events`);
    },
    databaseChecks(databaseId: string): string {
      return withRange(`/databases/${databaseId}/checks`);
    },
    databaseCheck(
      databaseId: string,
      checkGroupOrShorthand: string,
      checkName?: string,
    ): string {
      const checkGroupAndName =
        checkName !== undefined
          ? checkGroupOrShorthand + "/" + checkName
          : checkGroupOrShorthand;
      return withRange(`/databases/${databaseId}/checks/${checkGroupAndName}`);
    },
    databaseCheckAcknowledged(
      databaseId: string,
      checkGroupOrShorthand: string,
      checkName?: string,
    ): string {
      const checkGroupAndName =
        checkName !== undefined
          ? checkGroupOrShorthand + "/" + checkName
          : checkGroupOrShorthand;
      return withRange(
        `/databases/${databaseId}/checks/${checkGroupAndName}/acknowledged`,
      );
    },
    databaseCheckResolved(
      databaseId: string,
      checkGroupOrShorthand: string,
      checkName?: string,
    ): string {
      const checkGroupAndName =
        checkName !== undefined
          ? checkGroupOrShorthand + "/" + checkName
          : checkGroupOrShorthand;
      return withRange(
        `/databases/${databaseId}/checks/${checkGroupAndName}/resolved`,
      );
    },
    databaseIssue(
      databaseId: string,
      issueId: string,
      checkGroupOrShorthand: string,
      checkName?: string,
    ): string {
      const checkGroupAndName =
        checkName !== undefined
          ? checkGroupOrShorthand + "/" + checkName
          : checkGroupOrShorthand;
      return withRange(
        `/databases/${databaseId}/checks/${checkGroupAndName}/${issueId}`,
      );
    },
    databaseVacuumsBloat(databaseId: string): string {
      return withRange(`/databases/${databaseId}/vacuums/bloat`);
    },
    databaseVacuumsActivity(databaseId: string): string {
      return withRange(`/databases/${databaseId}/vacuums/activity`);
    },
    databaseVacuumsFreezing(databaseId: string): string {
      return withRange(`/databases/${databaseId}/vacuums/freezing`);
    },
    marketplaceLink(provider: string): string {
      return `/marketplace/${provider}/link`;
    },
    organization(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}`);
    },
    organizationAlerts(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/alerts`);
    },
    organizationAlertDestinations(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/alert-destinations`);
    },
    organizationAlertDestination(
      orgSlug: string,
      destinationId: string,
    ): string {
      return withRange(
        `/organizations/${orgSlug}/alert-destinations/${destinationId}`,
      );
    },
    organizationAlertDestinationNew(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/alert-destinations/new`);
    },
    organizationAlertRules(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/alert-rules`);
    },
    organizationAlertRule(orgSlug: string, alertRuleId: string): string {
      return withRange(`/organizations/${orgSlug}/alert-rules/${alertRuleId}`);
    },
    organizationAlertRuleNew(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/alert-rules/new`);
    },
    organizationApi(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/api`);
    },
    organizationOAuth(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/oauth`);
    },
    organizationDatabases(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/databases`);
    },
    organizationWorkbooks(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/workbooks`);
    },
    organizationEdit(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/edit`);
    },
    organizationIntegrations(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/integrations`);
    },
    organizationIntegrationsPagerduty(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/integrations/pagerduty`);
    },
    organizationMember(orgSlug: string, memberId: string): string {
      return withRange(`/organizations/${orgSlug}/members/${memberId}`);
    },
    organizationMemberEdit(orgSlug: string, memberId: string): string {
      return withRange(`/organizations/${orgSlug}/members/${memberId}/edit`);
    },
    organizationMembers(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/members`);
    },
    organizationMembersNew(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/members/new`);
    },
    organizationResendInvite(orgSlug: string, memberId: string): string {
      return withRange(
        `/organizations/${orgSlug}/members/${memberId}/resend_invite`,
      );
    },
    organizationAcceptInvite(orgSlug: string, inviteToken: string): string {
      return withRange(
        `organizations/${orgSlug}/members/accept/${inviteToken}`,
      );
    },
    organizationRole(orgSlug: string, roleId: string): string {
      return withRange(`/organizations/${orgSlug}/roles/${roleId}`);
    },
    organizationRoleEdit(orgSlug: string, roleId: string): string {
      return withRange(`/organizations/${orgSlug}/roles/${roleId}/edit`);
    },
    organizationRoleNew(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/roles/new`);
    },
    organizationRoles(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/roles`);
    },
    organizationServerGroups(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/servergroups`);
    },
    organizationServerGroupsNew(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/servergroups/new`);
    },
    organizationServerGroupsSettings(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/servergroups/settings`);
    },
    organizationServers(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/servers`);
    },
    organizationServersInstall(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/servers/install`);
    },
    organizationServersSettings(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/servers/settings`);
    },
    organizationServersNew(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/servers/new`);
    },
    organizationCollectorConfig(orgSlug: string): string {
      return `/organizations/${orgSlug}/servers/collector_config`;
    },
    organizationSetup(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/setup`);
    },
    organizationSubscription(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/subscription`);
    },
    organizationSubscriptionInvoices(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/subscription#invoices`);
    },
    organizationSubscriptionComparePlans(orgSlug: string): string {
      return withRange(`/organizations/${orgSlug}/subscription/compare`);
    },
    organizationSubscriptionUpdateBillingDetails(orgSlug: string): string {
      return `/organizations/${orgSlug}/subscription/update_billing_details`;
    },
    organizationSubscriptionUpdateCreditCard(orgSlug: string): string {
      return `/organizations/${orgSlug}/subscription/update_credit_card`;
    },
    organizationSubscriptionActivate(orgSlug: string, planId: string): string {
      return `/organizations/${orgSlug}/subscription/activate/${planId}`;
    },
    organizationSubscriptionCancel(orgSlug: string): string {
      return `/organizations/${orgSlug}/subscription/cancel`;
    },
    organizationSubscriptionInvoice(
      orgSlug: string,
      reference: string,
    ): string {
      return `/organizations/${orgSlug}/subscription/invoices/${reference}`;
    },
    organizationCheck(
      organizationSlug: string,
      checkGroupOrShorthand: string,
      checkName?: string,
    ): string {
      const checkGroupAndName =
        checkName !== undefined
          ? checkGroupOrShorthand + "/" + checkName
          : checkGroupOrShorthand;
      return withRange(
        `/organizations/${organizationSlug}/checks/${checkGroupAndName}`,
      );
    },
    organizationCheckAcknowledged(
      organizationSlug: string,
      checkGroupOrShorthand: string,
      checkName?: string,
    ): string {
      const checkGroupAndName =
        checkName !== undefined
          ? checkGroupOrShorthand + "/" + checkName
          : checkGroupOrShorthand;
      return withRange(
        `/organizations/${organizationSlug}/checks/${checkGroupAndName}/acknowledged`,
      );
    },
    organizationCheckResolved(
      organizationSlug: string,
      checkGroupOrShorthand: string,
      checkName?: string,
    ): string {
      const checkGroupAndName =
        checkName !== undefined
          ? checkGroupOrShorthand + "/" + checkName
          : checkGroupOrShorthand;
      return withRange(
        `/organizations/${organizationSlug}/checks/${checkGroupAndName}/resolved`,
      );
    },
    organizationCheckConfigure(
      organizationSlug: string,
      checkGroupOrShorthand: string,
      checkName?: string,
    ): string {
      const checkGroupAndName =
        checkName !== undefined
          ? checkGroupOrShorthand + "/" + checkName
          : checkGroupOrShorthand;
      return withRange(
        `/organizations/${organizationSlug}/checks/${checkGroupAndName}/configure`,
      );
    },
    organizationChecks(organizationSlug: string): string {
      return withRange(`/organizations/${organizationSlug}/checks`);
    },
    organizationsNew(): string {
      return withRange("/organizations/new");
    },
    organizations(): string {
      return withRange(`/organizations`);
    },

    root(): string {
      return withRange("/");
    },

    server(serverId: string): string {
      return withRange(`/servers/${serverId}`);
    },
    serverIndexAdvisor(serverId: string): string {
      return withRange(`/servers/${serverId}/indexadvisor`);
    },
    serverQueries(serverId: string): string {
      return withRange(`/servers/${serverId}/queries`);
    },
    serverQueriesExplains(serverId: string): string {
      return withRange(`/servers/${serverId}/queries/explains`);
    },
    serverQueriesAdvisor(serverId: string): string {
      return withRange(`/servers/${serverId}/queries/advisor`);
    },
    serverWorkbooks(serverId: string): string {
      return withRange(`/servers/${serverId}/workbooks`);
    },
    serverQueriesSearch(serverId: string): string {
      return withRange(`/servers/${serverId}/queries/search`);
    },
    serverSchema(serverId: string): string {
      return withRange(`/servers/${serverId}/schema`);
    },
    serverConnections(serverId: string): string {
      return withRange(`/servers/${serverId}/connections`);
    },
    serverEdit(serverId: string): string {
      return withRange(`/servers/${serverId}/edit`);
    },
    collectorSettings(serverId: string): string {
      return withRange(`/servers/${serverId}/collector_settings`);
    },
    serverLogs(serverId: string, logClassification?: string): string {
      const classificationPart = logClassification
        ? `/${logClassification}`
        : "";
      return withRange(`/servers/${serverId}/logs${classificationPart}`);
    },
    serverReplication(serverId: string): string {
      return withRange(`/servers/${serverId}/replication`);
    },
    serverVacuum(serverId: string, vacuumId: string): string {
      return withRange(`/servers/${serverId}/vacuums/${vacuumId}`);
    },
    serverVacuums(serverId: string): string {
      return withRange(`/servers/${serverId}/vacuums`);
    },
    serverVacuumsActivity(serverId: string): string {
      return withRange(`/servers/${serverId}/vacuums/activity`);
    },
    serverVacuumsBloat(serverId: string): string {
      return withRange(`/servers/${serverId}/vacuums/bloat`);
    },
    serverVacuumsPerformance(serverId: string): string {
      return withRange(`/servers/${serverId}/vacuums/performance`);
    },
    serverVacuumsFreezing(serverId: string): string {
      return withRange(`/servers/${serverId}/vacuums/freezing`);
    },
    serverRole(serverId: string, roleId: string): string {
      return withRange(`/servers/${serverId}/roles/${roleId}`);
    },
    serverRoles(serverId: string): string {
      return withRange(`/servers/${serverId}/roles`);
    },
    serverConfig(serverId: string): string {
      return withRange(`/servers/${serverId}/config`);
    },
    serverConfigSetting(serverId: string, setting: string): string {
      return withRange(`/servers/${serverId}/config/${setting}`);
    },
    serverSystem(
      serverId: string,
      detail?: "cpu" | "memory" | "storage" | "network",
    ): string {
      const detailStr = detail === undefined ? "" : `/${detail}`;
      return withRange(`/servers/${serverId}/system${detailStr}`);
    },
    serverSetup(serverId: string): string {
      return withRange(`/servers/${serverId}/setup`);
    },
    serverSetupLogInsights(serverId: string): string {
      return withRange(`/servers/${serverId}/setup/log-insights`);
    },
    serverSetupAutomatedExplain(serverId: string): string {
      return withRange(`/servers/${serverId}/setup/explain`);
    },
    serverSnapshot(serverId: string, snapshotId: string): string {
      return withRange(`/servers/${serverId}/snapshots/${snapshotId}`);
    },
    serverSnapshots(serverId: string): string {
      return withRange(`/servers/${serverId}/snapshots`);
    },
    serverAlerts(serverId: string): string {
      return withRange(`/servers/${serverId}/alerts`);
    },
    serverAlertRules(serverId: string): string {
      return withRange(`/servers/${serverId}/alert-rules`);
    },
    serverAlertDestinations(serverId: string): string {
      return withRange(`/servers/${serverId}/alert-destinations`);
    },
    serverChecks(serverId: string): string {
      return withRange(`/servers/${serverId}/checks`);
    },
    serverCheck(
      serverId: string,
      checkGroupOrShorthand: string,
      checkName?: string,
    ): string {
      const checkGroupAndName =
        checkName !== undefined
          ? checkGroupOrShorthand + "/" + checkName
          : checkGroupOrShorthand;
      return withRange(`/servers/${serverId}/checks/${checkGroupAndName}`);
    },
    serverCheckAcknowledged(
      serverId: string,
      checkGroupOrShorthand: string,
      checkName?: string,
    ): string {
      const checkGroupAndName =
        checkName !== undefined
          ? checkGroupOrShorthand + "/" + checkName
          : checkGroupOrShorthand;
      return withRange(
        `/servers/${serverId}/checks/${checkGroupAndName}/acknowledged`,
      );
    },
    serverCheckResolved(
      serverId: string,
      checkGroupOrShorthand: string,
      checkName?: string,
    ): string {
      const checkGroupAndName =
        checkName !== undefined
          ? checkGroupOrShorthand + "/" + checkName
          : checkGroupOrShorthand;
      return withRange(
        `/servers/${serverId}/checks/${checkGroupAndName}/resolved`,
      );
    },
    serverCheckConfigure(
      serverId: string,
      checkGroupOrShorthand: string,
      checkName?: string,
    ): string {
      const checkGroupAndName =
        checkName !== undefined
          ? checkGroupOrShorthand + "/" + checkName
          : checkGroupOrShorthand;
      return withRange(
        `/servers/${serverId}/checks/${checkGroupAndName}/configure`,
      );
    },
    serverIssue(
      serverId: string,
      issueId: string,
      checkGroupOrShorthand: string,
      checkName?: string,
    ): string {
      const checkGroupAndName =
        checkName !== undefined
          ? checkGroupOrShorthand + "/" + checkName
          : checkGroupOrShorthand;
      return withRange(
        `/servers/${serverId}/checks/${checkGroupAndName}/${issueId}`,
      );
    },
    serverGroupDatabases(serverGroupId: string): string {
      return withRange(`/servergroups/${serverGroupId}/databases`);
    },
    serverGroupEdit(serverGroupId: string): string {
      return withRange(`/servergroups/${serverGroupId}/edit`);
    },
    serverGroupQueries(serverGroupId: string, datname?: string): string {
      return withRange(`/servergroups/${serverGroupId}/queries`, {
        search: { datname },
      });
    },
    serverGroupServers(serverGroupId: string): string {
      return withRange(`/servergroups/${serverGroupId}/servers`);
    },
    serverGroupWorkbooks(serverGroupId: string): string {
      return withRange(`/servergroups/${serverGroupId}/workbooks`);
    },
    users(): string {
      return withRange("/users");
    },
    userEdit(): string {
      return withRange("/users/edit");
    },
  };
};

export const useUnauthenticatedRoutes = () => {
  return {
    users(): string {
      return "/users";
    },
    userSignUp(): string {
      return "/users/sign_up";
    },
    userSignIn(): string {
      return "/users/sign_in";
    },
    userPasswordReset(): string {
      return "/users/password/new";
    },
  };
};

// Returns the matched route *pattern* (e.g. "servers/:serverId/queries")
//
// TODO: There might be a way to avoid direct Router context access with some
// refactoring - this is done like this for now to aid with migration from
// React Router v5 (which allowed access to the route path), and v6 (which
// does not appear to allow access). If we move to a data router, useMatches()
// would be the supported way to get this.
export const useRoutePattern = (): string => {
  const matches = useContext(UNSAFE_RouteContext).matches;
  return matches
    .map((m) => m.route.path)
    .filter((p) => p != undefined)
    .join("/");
};

// Returns a route thats conceptually similar as the current route, but for a different organization/server/database.
//
// Note that this also supports going across different reference types (e.g. query perf view for a particular server
// to query perf view for some database ID), if available. Falls back to the root path for the particular type if
// no match can be made.
export const useRelatedRoute = () => {
  const params = useParams();

  const pathname = useRoutePattern();

  const {
    server: serverRoot,
    database: databaseRoot,
    organization: organizationRoot,
    serverGroupServers: serverGroupRoot,
  } = useRoutes();
  const [routeState] = useContext(RouteStateContext);
  const ts = routeState.search.t;

  const withRange = (
    url: string,
    opts?: {
      search: Record<string, string | undefined>;
    },
  ): string => {
    const search = {
      ...opts?.search,
    };
    if (ts) {
      search.t = ts;
    }

    return formatPath(url, search);
  };

  const relatedDatabaseRoute = (databaseId: string): string => {
    if (!params || !pathname) {
      return databaseRoot(databaseId);
    }

    if (
      isEqual(Object.keys(params), ["databaseId"]) ||
      isEqual(Object.keys(params), ["databaseId", "checkGroup", "checkName"]) ||
      isEqual(Object.keys(params), ["databaseId", "classification"])
    ) {
      return withRange(generatePath(pathname, { ...params, databaseId }));
    }
    const mappedPath = mapToRelatedDatabasePath(pathname);
    if (mappedPath) {
      return withRange(
        generatePath(mappedPath, {
          ...params,
          databaseId,
        }),
      );
    }

    return databaseRoot(databaseId);
  };

  const relatedServerRoute = (serverId: string): string => {
    if (!params || !pathname) {
      return serverRoot(serverId);
    }

    if (
      isEqual(Object.keys(params), ["serverId"]) ||
      isEqual(Object.keys(params), ["serverId", "checkGroup", "checkName"]) ||
      isEqual(Object.keys(params), ["serverId", "classification"]) ||
      isEqual(Object.keys(params), ["serverId", "settingName"])
    ) {
      return withRange(generatePath(pathname, { ...params, serverId }));
    }
    const mappedPath = mapToRelatedServerPath(pathname);
    if (mappedPath) {
      return withRange(
        generatePath(mappedPath, {
          ...params,
          serverId,
        }),
      );
    }

    return serverRoot(serverId);
  };

  const relatedServerGroupRoute = (serverGroupId: string) => {
    if (!params || !pathname) {
      return serverGroupRoot(serverGroupId);
    }

    const mappedPath = mapToRelatedServerGroupPath(pathname);
    if (mappedPath) {
      return withRange(
        generatePath(mappedPath, {
          ...params,
          serverGroupId,
        }),
      );
    }

    return serverGroupRoot(serverGroupId);
  };

  const relatedOrganizationRoute = (slug: string): string => {
    if (!params || !pathname) {
      return organizationRoot(slug);
    }

    if (
      isEqual(Object.keys(params), ["slug"]) ||
      isEqual(Object.keys(params), ["slug", "checkGroup", "checkName"])
    ) {
      return withRange(generatePath(pathname, { ...params, slug }));
    }
    const mappedPath = mapToRelatedOrganizationPath(pathname);
    if (mappedPath) {
      return withRange(
        generatePath(mappedPath, {
          ...params,
          slug,
        }),
      );
    }

    return organizationRoot(slug);
  };

  return {
    relatedDatabaseRoute,
    relatedServerRoute,
    relatedOrganizationRoute,
    relatedServerGroupRoute,
  };
};

export function useLimitedRetention(retention: moment.Duration) {
  const location = useLocation();
  const path = location.pathname;
  const limitedRetention =
    /^\/databases\/.*\/(?:wait_events|backends)|\/servers\/.*\/connections/.test(
      path,
    )
      ? retentionLimits.connections
      : /^\/(?:servers|databases)\/.*\/logs/.test(path)
      ? retentionLimits.logs
      : retentionLimits.default;

  const actualRetention =
    limitedRetention.valueOf() < retention.valueOf()
      ? limitedRetention
      : retention;

  return actualRetention;
}

// Source paths that can map to a certain target database path
const mapToRelatedDatabasePath = (path: string): string | null => {
  switch (path) {
    case "/servers/:serverId/queries":
    case "/servers/:serverId/queries/explains":
    case "/servers/:serverId/queries/search":
    case "/servers/:serverId/workbooks":
    case "/servers/:serverId/logs":
    case "/servers/:serverId/logs/:classification":
    case "/servers/:serverId/checks":
    case "/servers/:serverId/checks/:checkGroup/:checkName":
    case "/servers/:serverId/checks/:checkGroup/:checkName/acknowledged":
    case "/servers/:serverId/checks/:checkGroup/:checkName/resolved":
    case "/servers/:serverId/checks/:checkGroup/:checkName/configure":
    case "/servers/:serverId/indexadvisor":
    case "/servers/:serverId/vacuums/activity":
    case "/servers/:serverId/vacuums/bloat":
    case "/servers/:serverId/vacuums/freezing":
      return path.replace("/servers/:serverId/", "/databases/:databaseId/");
    case "/servers/:serverId/schema":
      return "/databases/:databaseId/tables";
    case "/servers/:serverId/connections":
      return "/databases/:databaseId/backends";
    case "/organizations/:slug/checks":
    case "/organizations/:slug/checks/:checkGroup/:checkName":
    case "/organizations/:slug/checks/:checkGroup/:checkName/acknowledged":
    case "/organizations/:slug/checks/:checkGroup/:checkName/resolved":
    case "/organizations/:slug/checks/:checkGroup/:checkName/configure":
    case "/organizations/:slug/workbooks":
      return path.replace("/organizations/:slug/", "/databases/:databaseId/");
    case "/servergroups/:serverGroupId/workbooks":
      return path.replace(
        "/servergroups/:serverGroupId/",
        "/databases/:databaseId/",
      );
    // Multi-parameter paths that should go to the top-level within the database context
    case "/databases/:databaseId/queries/:queryId":
    case "/databases/:databaseId/queries/:queryId/indexcheck":
    case "/databases/:databaseId/queries/:queryId/samples":
    case "/databases/:databaseId/queries/:queryId/explains":
    case "/databases/:databaseId/queries/:queryId/explains/:explainId":
    case "/databases/:databaseId/queries/:queryId/tags":
    case "/databases/:databaseId/queries/:queryId/logs":
      return "/databases/:databaseId/queries";
    case "/databases/:databaseId/tables/:tableId":
    case "/databases/:databaseId/tables/:tableId/partitions":
    case "/databases/:databaseId/tables/:tableId/queries":
    case "/databases/:databaseId/tables/:tableId/columns":
    case "/databases/:databaseId/tables/:tableId/indexes":
    case "/databases/:databaseId/tables/:tableId/constraints":
    case "/databases/:databaseId/tables/:tableId/vacuum":
      return "/databases/:databaseId/tables";
    case "/databases/:databaseId/indexes/:schemaIndexId":
      return "/databases/:databaseId/indexes";
    case "/databases/:databaseId/views/:viewId":
    case "/databases/:databaseId/views/:viewId/columns":
    case "/databases/:databaseId/views/:viewId/indexes":
      return "/databases/:databaseId/views";
    case "/databases/:databaseId/functions/:schemaFunctionId":
      return "/databases/:databaseId/functions";
    case "/databases/:databaseId/backends/:locator":
    case "/databases/:databaseId/backends/:locator/queries":
    case "/databases/:databaseId/backends/:locator/wait_events":
    case "/databases/:databaseId/backends/:locator/logs":
      return "/databases/:databaseId/backends";
  }
  return null;
};

// Source paths that can map to a certain target server path
const mapToRelatedServerPath = (path: string): string | null => {
  switch (path) {
    case "/databases/:databaseId/queries":
    case "/databases/:databaseId/queries/advisor":
    case "/databases/:databaseId/queries/explains":
    case "/databases/:databaseId/queries/search":
    case "/databases/:databaseId/workbooks":
    case "/databases/:databaseId/logs":
    case "/databases/:databaseId/logs/:classification":
    case "/databases/:databaseId/checks":
    case "/databases/:databaseId/checks/:checkGroup/:checkName":
    case "/databases/:databaseId/checks/:checkGroup/:checkName/acknowledged":
    case "/databases/:databaseId/checks/:checkGroup/:checkName/resolved":
    case "/databases/:databaseId/checks/:checkGroup/:checkName/configure":
    case "/databases/:databaseId/indexadvisor":
    case "/databases/:databaseId/vacuums/activity":
    case "/databases/:databaseId/vacuums/bloat":
    case "/databases/:databaseId/vacuums/freezing":
      return path.replace("/databases/:databaseId/", "/servers/:serverId/");
    case "/databases/:databaseId/queries/:queryId":
    case "/databases/:databaseId/queries/:queryId/indexcheck":
    case "/databases/:databaseId/queries/:queryId/samples":
    case "/databases/:databaseId/queries/:queryId/explains":
    case "/databases/:databaseId/queries/:queryId/explains/:explainId":
    case "/databases/:databaseId/queries/:queryId/tags":
    case "/databases/:databaseId/queries/:queryId/logs":
      return "/servers/:serverId/queries";
    case "/databases/:databaseId/tables":
    case "/databases/:databaseId/tables/:tableId":
    case "/databases/:databaseId/tables/:tableId/partitions":
    case "/databases/:databaseId/tables/:tableId/queries":
    case "/databases/:databaseId/tables/:tableId/columns":
    case "/databases/:databaseId/tables/:tableId/indexes":
    case "/databases/:databaseId/tables/:tableId/constraints":
    case "/databases/:databaseId/tables/:tableId/vacuum":
    case "/databases/:databaseId/indexes":
    case "/databases/:databaseId/indexes/:schemaIndexId":
    case "/databases/:databaseId/views":
    case "/databases/:databaseId/views/:viewId":
    case "/databases/:databaseId/views/:viewId/columns":
    case "/databases/:databaseId/views/:viewId/indexes":
    case "/databases/:databaseId/functions":
    case "/databases/:databaseId/functions/:schemaFunctionId":
      return "/servers/:serverId/schema";
    case "/databases/:databaseId/backends":
    case "/databases/:databaseId/backends/:locator":
    case "/databases/:databaseId/backends/:locator/queries":
    case "/databases/:databaseId/backends/:locator/wait_events":
    case "/databases/:databaseId/backends/:locator/logs":
    case "/databases/:databaseId/wait_events":
      return "/servers/:serverId/connections";
    case "/organizations/:slug/checks":
    case "/organizations/:slug/checks/:checkGroup/:checkName":
    case "/organizations/:slug/checks/:checkGroup/:checkName/acknowledged":
    case "/organizations/:slug/checks/:checkGroup/:checkName/resolved":
    case "/organizations/:slug/checks/:checkGroup/:checkName/configure":
    case "/organizations/:slug/alerts":
    case "/organizations/:slug/alert-rules":
    case "/organizations/:slug/alert-destinations":
    case "/organizations/:slug/workbooks":
      return path.replace("/organizations/:slug/", "/servers/:serverId/");
    case "/servergroups/:serverGroupId/workbooks":
      return path.replace(
        "/servergroups/:serverGroupId/",
        "/servers/:serverId/",
      );
    case "/organizations/:slug/servers/settings":
    case "/servergroups/:serverGroupId/edit":
      return "/servers/:serverId/edit";
    case "/servergroups/:serverGroupId/queries":
      return "/servers/:serverId/queries";
    // Multi-parameter paths that should go to the top-level within the database context
    case "/servers/:serverId/vacuums/:vacuumRunIdentity":
      return "/servers/:serverId/vacuums";
    case "/servers/:serverId/roles/:id":
      return "/servers/:serverId/roles";
    case "/servers/:serverId/snapshots/:snapshotId":
      return "/servers/:serverId/snapshots";
  }
  return null;
};

// Source paths that can map to a certain target server group path
const mapToRelatedServerGroupPath = (path: string): string | null => {
  switch (path) {
    case "/servers/:serverId/queries":
    case "/servergroups/:serverGroupId/queries":
      return "/servergroups/:serverGroupId/queries";
    case "/databases/:databaseId/workbooks":
    case "/servers/:serverId/workbooks":
    case "/organizations/:slug/workbooks":
    case "/servergroups/:serverGroupId/workbooks":
      return "/servergroups/:serverGroupId/workbooks";
    case "/organizations/:slug/servers/settings":
    case "/servers/:serverId/edit":
    case "/servergroups/:serverGroupId/edit":
      return "/servergroups/:serverGroupId/edit";
  }
  return null;
};

// Source paths that can map to a certain target organization path
const mapToRelatedOrganizationPath = (path: string): string | null => {
  switch (path) {
    case "/databases/:databaseId/checks":
    case "/databases/:databaseId/checks/:checkGroup/:checkName":
    case "/databases/:databaseId/checks/:checkGroup/:checkName/acknowledged":
    case "/databases/:databaseId/checks/:checkGroup/:checkName/resolved":
    case "/databases/:databaseId/checks/:checkGroup/:checkName/configure":
      return path.replace("/databases/:databaseId/", "/organizations/:slug/");
    case "/servers/:serverId/checks":
    case "/servers/:serverId/checks/:checkGroup/:checkName":
    case "/servers/:serverId/checks/:checkGroup/:checkName/acknowledged":
    case "/servers/:serverId/checks/:checkGroup/:checkName/resolved":
    case "/servers/:serverId/checks/:checkGroup/:checkName/configure":
    case "/servers/:serverId/alerts":
    case "/servers/:serverId/alert-rules":
    case "/servers/:serverId/alert-destinations":
      return path.replace("/servers/:serverId/", "/organizations/:slug/");
    case "/servers/:serverId/edit":
      return "/organizations/:slug/servers/settings";
    case "/servergroups/:serverGroupId/edit":
      return "/organizations/:slug/servergroups/settings";
    // Multi-parameter paths that should go to the top-level within the organization context
    case "/organizations/:slug/alert-destinations/:destinationId":
      return "/organizations/:slug/alert-destinations";
    case "/organizations/:slug/alert-rules/:alertRuleId":
      return "/organizations/:slug/alert-rules";
    case "/organizations/:slug/members/:id":
    case "/organizations/:slug/members/:id/edit":
      return "/organizations/:slug/members";
    case "/organizations/:slug/roles/:id":
    case "/organizations/:slug/roles/:id/edit":
      return "/organizations/:slug/roles";
    case "/organizations/:slug/subscription/activate/:plan_id":
      return "/organizations/:slug/subscription";
    case "/databases/:databaseId/workbooks":
      return path.replace("/databases/:databaseId/", "/organizations/:slug/");
    case "/servers/:serverId/workbooks":
      return path.replace("/servers/:serverId/", "/organizations/:slug/");
    case "/servergroups/:serverGroupId/workbooks":
      return path.replace(
        "/servergroups/:serverGroupId/",
        "/organizations/:slug/",
      );
  }

  return null;
};

function formatPath(path: string, search: Record<string, string | undefined>) {
  const params = new URLSearchParams();
  Object.entries(search).forEach(([k, v]) => {
    if (v === undefined) {
      return;
    }
    params.set(k, v);
  });
  if (params.size === 0) {
    return path;
  }
  return path + "?" + params.toString();
}
